import { useMemo } from "react";

import { BulkEntityActionItem } from "components/BulkActions/types";
import { useBulkDiscardRun } from "shared/Run/useDiscardRun";
import { useBulkSetPrioritizeRun } from "shared/Run/useSetPrioritizeRun";
import useCanDiscardRun from "shared/Run/access/useCanDiscardRun";
import useCanPrioritizeRun from "shared/Run/access/useCanPrioritizeRun";

import { BulkItemRunWithPosition, BulkWorkerPoolQueuedRunsActions } from "./types";

const useWorkerRunsBulkActions = (isPublicWorkerPool: boolean) => {
  const [runDiscard] = useBulkDiscardRun();
  const [runSetPrioritize] = useBulkSetPrioritizeRun();
  const canDiscard = useCanDiscardRun();
  const canPrioritize = useCanPrioritizeRun();

  return useMemo<
    Array<BulkEntityActionItem<BulkWorkerPoolQueuedRunsActions, BulkItemRunWithPosition, undefined>>
  >(
    () => [
      {
        key: BulkWorkerPoolQueuedRunsActions.PRIORITIZE,
        title: "Prioritize",
        resultTitle: "Prioritize runs results",
        condition: (runWithPosition) => {
          // Disable if run is already prioritized
          if (runWithPosition.run.isPrioritized) {
            return { result: false, message: "Run is already prioritized" };
          }

          return canPrioritize({
            space: runWithPosition.space,
            isRunFinished: runWithPosition.run.finished,
            hasWorkerPool: true,
            isPublicWorkerPool,
          });
        },
        mutation: (run) => runSetPrioritize({ stack: run.stackId, run: run.id, prioritize: true }),
      },
      {
        key: BulkWorkerPoolQueuedRunsActions.DE_PRIORITIZE,
        title: "De-prioritize",
        resultTitle: "De-prioritize runs results",
        condition: (runWithPosition) => {
          // Disable if run is not prioritized
          if (!runWithPosition.run.isPrioritized) {
            return { result: false, message: "Run is not prioritized" };
          }

          return canPrioritize({
            space: runWithPosition.space,
            isRunFinished: runWithPosition.run.finished,
            hasWorkerPool: true,
            isPublicWorkerPool,
          });
        },
        mutation: (run) => runSetPrioritize({ stack: run.stackId, run: run.id, prioritize: false }),
      },
      {
        key: BulkWorkerPoolQueuedRunsActions.DISCARD,
        title: "Discard",
        resultTitle: "Discard runs results",
        condition: (runWithPosition) =>
          canDiscard(runWithPosition.run, {
            space: runWithPosition.space.id,
            spaceDetails: runWithPosition.space,
          }),
        mutation: (run) => runDiscard({ stack: run.stackId, run: run.id, note: null }),
      },
    ],
    [isPublicWorkerPool, runDiscard, runSetPrioritize, canDiscard, canPrioritize]
  );
};

export default useWorkerRunsBulkActions;
